import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { FileText, MessageCircleQuestion, Package, ShieldCheck, User } from 'lucide-react';
import { AppShell, NavItem } from '@/components/common';
import { useAuth } from '@/context/AuthContext';
import { formatIndianMobile } from '@/lib/support';
import NotificationsProvider from '@/components/customer-dashboard/NotificationsProvider';

const NAV_ITEMS: NavItem[] = [
  { to: '/customer/policies', label: 'My Plans', shortLabel: 'Plans', icon: ShieldCheck },
  { to: '/customer/orders', label: 'Orders', shortLabel: 'Orders', icon: Package },
  { to: '/customer/claims', label: 'Claims', shortLabel: 'Claims', icon: FileText },
  { to: '/customer/support', label: 'Help & Support', shortLabel: 'Help', icon: MessageCircleQuestion },
  { to: '/customer/settings', label: 'Account', shortLabel: 'Account', icon: User, group: 'account' },
];

/** Pages already rebuilt with the design-system components. */
const REDESIGNED_PAGES = [
  /^\/customer\/policies(\/[^/]+)?\/?$/,
  /^\/customer\/claims(\/[^/]+)?\/?$/,
  /^\/customer\/orders(\/[^/]+(\/shipment|\/return(\/status)?)?)?\/?$/,
  /^\/customer\/support\/?$/,
  /^\/customer\/settings(\/notifications|\/gst(\/edit|\/invoices\/[^/]+)?)?\/?$/,
  /^\/customer\/notifications\/?$/,
];

/**
 * Pages not yet rebuilt in the new design still expect the old dark background.
 * TODO: remove this wrapper once every /customer page uses the design-system components.
 */
const LegacyPage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  if (REDESIGNED_PAGES.some((page) => page.test(pathname))) return <>{children}</>;
  return <div className="tw-min-h-full tw-overflow-hidden tw-rounded-2xl tw-bg-[#202020] tw-text-gray-200">{children}</div>;
};

const CustomerDashboard: React.FC = () => {
  const { user, loginType, logout } = useAuth();

  // Guests (magic-link / return flows) get the page without navigation, as before.
  if (loginType === 'token') {
    return (
      <div className="tw-min-h-screen tw-bg-[#202020]">
        <Outlet />
      </div>
    );
  }

  const phone = formatIndianMobile(user?.phone);
  return (
    <NotificationsProvider>
      <AppShell
        items={NAV_ITEMS}
        user={{ name: user?.name, contact: phone || user?.email }}
        accountPath="/customer/settings"
        logoutDescription={
          phone ? `You’ll need a new OTP on ${phone} to sign in again.` : 'You’ll need to sign in again to see your plans and claims.'
        }
        onLogout={logout}
      >
        <LegacyPage>
          <Outlet />
        </LegacyPage>
      </AppShell>
    </NotificationsProvider>
  );
};

export default CustomerDashboard;
